import React from 'react';
import {
  CustomerMessagePayload,
  getMediumSchema,
  Medium,
  Mediums,
} from '../../types/global';
import useAwaitedForm from '../useAwaitedForm';

interface Options {
  mediums?: Medium[];
}

function useDeliveryMethods({
  mediums = [Mediums.EMAIL, Mediums.SMS],
}: Options = {}) {
  const asyncForm = useAwaitedForm();

  const getMethods = React.useCallback(
    async ({
      customer,
      title,
    }: {
      title: string;
      customer: {
        email: string | null;
        phoneNumber: string;
      };
    }): Promise<CustomerMessagePayload> => {
      const completeMethods = mediums.filter(method => {
        if (method === Mediums.EMAIL && customer.email) return true;
        if (method === Mediums.SMS && customer.phoneNumber) return true;
        return false;
      });
      if (
        mediums.length === 1 &&
        completeMethods.length === 1 &&
        mediums[0] === completeMethods[0]
      ) {
        return { mediums: completeMethods, email: null };
      }

      const validationSchema = getMediumSchema({
        requireEmail: !customer.email,
        requirePhone: !customer.phoneNumber,
      });

      const result = await asyncForm({
        form: {
          title,
          initialValues: {
            mediums,
            email: customer.email,
            phone: customer.phoneNumber,
          },
          inputRows: [
            [
              {
                inputType: 'multiSelect',
                label: 'Send via',
                name: 'mediums',
                options: [
                  {
                    label: 'Email',
                    value: 'email',
                  },
                  {
                    label: 'SMS',
                    value: 'sms',
                  },
                ],
                allText: 'Email and SMS',
                showSelectAll: false,
                autoWidth: false,
                required: true,
                disabled: mediums.length === 1,
              },
            ],
            [
              {
                inputType: 'text',
                type: 'email',
                label: 'Email',
                name: 'email',
                placeholder: 'Enter email',
                required: true,
                isVisible: vals =>
                  vals.mediums.includes(Mediums.EMAIL) && !customer.email,
              },
            ],
            [
              {
                inputType: 'text',
                type: 'tel',
                label: 'Phone',
                name: 'phone',
                placeholder: 'Enter phone',
                required: true,
                isVisible: vals =>
                  vals.mediums.includes(Mediums.SMS) && !customer.phoneNumber,
              },
            ],
          ],
          validationSchema,
          submitButtonText: 'Send',
        },
        allowUntouched: true,
      });
      if (!result) return { mediums: [], email: null, phone: null };

      return result;
    },
    [asyncForm, mediums],
  );

  return getMethods;
}

export default useDeliveryMethods;
